import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles, X } from "lucide-react";
import useTier from "../../hooks/useTier";

function daysLeft(iso) {
  if (!iso) return null;
  try {
    const end = new Date(iso).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
}

/**
 * Beta banner shown app-wide when BETA_MODE is on and the user has an active trial.
 * Dismissible per-session via sessionStorage.
 */
export default function BetaBanner() {
  const { t } = useTranslation();
  const { betaMode, trialActive, trialEndsAt } = useTier();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(sessionStorage.getItem("mp_beta_dismissed") === "1");
  }, []);

  if (!betaMode || !trialActive || dismissed) return null;

  const left = daysLeft(trialEndsAt);

  return (
    <div
      className="relative z-30 bg-gradient-to-r from-blue-600/90 via-cyan-600/90 to-indigo-600/90 border-b border-blue-400/40 text-white"
      data-testid="beta-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2.5 text-sm">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/15 backdrop-blur border border-white/20 text-[10px] font-mono uppercase tracking-wider">
            <Sparkles size={11}/> Beta
          </span>
          <span className="font-medium">
            {t("betaBanner.message")}
            {left !== null && (
              <span className="ml-1.5 px-2 py-0.5 rounded bg-white/15 text-xs font-mono">
                {t("betaBanner.daysLeft", { count: left })}
              </span>
            )}
          </span>
        </div>
        <button
          onClick={() => { sessionStorage.setItem("mp_beta_dismissed", "1"); setDismissed(true); }}
          className="text-white/80 hover:text-white p-1 rounded-md hover:bg-white/10 transition-colors"
          aria-label={t("common.closeBetaBanner")}
          data-testid="beta-banner-close"
        >
          <X size={16}/>
        </button>
      </div>
    </div>
  );
}
